import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#0B1E3C] to-[#11263D] text-white font-sans px-4 relative overflow-hidden">
      {/* Dynamic Background Waves */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-purple-800/20 via-transparent to-transparent animate-pulse" />
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-blue-800/20 via-transparent to-transparent animate-pulse" />
      </div>

      {/* Sticky Header */}
      <header className="sticky top-0 z-50 flex flex-wrap justify-between items-center py-6 px-4 w-full bg-transparent">
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-extrabold text-white tracking-wide">Heradexia</h1>
        </div>
        <nav className="space-x-6 text-sm font-medium mt-2 sm:mt-0">
          <Link to="/" className="hover:text-[#8BC53F]">Join Waitlist</Link>
          <Link to="/how-it-works" className="hover:text-[#8BC53F]">How It Works</Link>
          <Link to="/use-demo" className="hover:text-[#8BC53F]">Use Demo</Link>
        </nav>
      </header>

      <main className="max-w-4xl mx-auto py-20 relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-4xl md:text-5xl font-extrabold mb-6 text-center"
        >
          How Heradexia Works
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-lg text-gray-300 mb-12 text-center max-w-2xl mx-auto"
        >
          Discover how Heradexia combines visual learning, XP rewards, and meaningful mentorship to reshape modern education.
        </motion.p>

        {/* Vision Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="bg-[#112240] bg-opacity-90 p-10 rounded-2xl shadow-xl border border-[#1F2937] mb-10"
        >
          <h3 className="text-2xl font-bold text-purple-300 mb-4">Our Vision</h3>
          <p className="text-gray-300">
            Heradexia's vision is to transform distractions into powerful tools for learning through reliable, high-quality content turning the scrolling experience of social media into a source of engaging, visual education. Students no longer have to leave their favorite platforms to study. Heradexia meets them there and fills the feed with value. Heradexia is the foundation content block of a greater educational ecosystem being developed with the goal to transform modern day education forever.
          </p>
        </motion.section>

        {/* Origin Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1 }}
          className="bg-[#1B2E45] bg-opacity-90 p-10 rounded-2xl shadow-xl border border-[#1F2937] mb-10"
        >
          <h3 className="text-2xl font-bold text-yellow-400 mb-4">The Origin of Heradexia</h3>
          <p className="text-gray-300">
            Heradexia is a name rooted in classical Greek and Latin influences, crafted to reflect the platform’s mission of structured, transformative learning.
            The first part, “Hera,” draws from the Greek figure symbolizing wisdom, guidance, and protection—an ideal representation of mentorship.
            The original suffix, inspired by “erudire” (Latin for “to educate”) and “radia” (to radiate), evolved into “dexia,” the Greek word for “right hand,” symbolizing support, skill, and capability.
            Together, Heradexia means a guided, radiant path of education—empowering students through wisdom, structure, and strength.
          </p>
        </motion.section>

        {/* How It Works Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="bg-[#122B3E] bg-opacity-90 p-10 rounded-2xl shadow-xl border border-[#1F2937] mb-10"
        >
          <h3 className="text-2xl font-bold text-green-400 mb-4">How It Works</h3>
          <p className="text-gray-300 mb-4">
            Heradexia is built to be intuitive and rewarding. Here's the flow:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Sign up and choose your role: <strong>Learner</strong> or <strong>Mentor</strong>.</li>
            <li>As a Learner, browse or search for Courses based on your level and interest or just scroll through the feed like TikTok and earn XP.</li>
            <li>Choose a learning path: Self-study with AI, with a Tutor, or via Quick Quizzes.</li>
            <li>Watch short-form videos and complete tasks to earn XP and keep your streak alive.</li>
            <li>Track your progress, earn badges, and unlock exclusive content.</li>
            <li>Mentors can upload videos, guide learners, and earn bonuses based on performance.</li>
          </ul>
        </motion.section>

        {/* Method Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 1 }}
          className="bg-[#122B3E] bg-opacity-90 p-10 rounded-2xl shadow-xl border border-[#1F2937]"
        >
          <h3 className="text-2xl font-bold text-purple-300 mb-4">The Heradexia Method</h3>
          <p className="text-gray-300 mb-4">
            Our 6-step session structure is based on real classroom effectiveness, paired with digital learning trends. Here’s what a typical Heradexia-guided lesson looks like:
          </p>
          <ol className="list-decimal list-inside text-gray-300 space-y-2">
            <li><strong>Check-in:</strong> Start with a quick greeting and pulse-check.</li>
            <li><strong>Short Videos:</strong> Watch Heradexia-style 30–90s videos for ~15–20 min.</li>
            <li><strong>Interactive Game:</strong> Reinforce learning through a Kahoot/Blooket session.</li>
            <li><strong>Guided Practice:</strong> Dive into regular problems and warm-up questions.</li>
            <li><strong>Mentor Time:</strong> Ask questions, discuss tough concepts, and tackle a critical thinking problem.</li>
            <li><strong>Closing:</strong> Reflect, review your progress, and get encouragement for next steps. Get XP!</li>
          </ol>
        </motion.section>
      </main>

      <footer className="mt-20 text-center text-xs text-gray-400 py-4 border-t border-gray-700">
        © 2025 Heradexia • Built with love and purpose.
      </footer>
    </div>
  );
}


